import autoTable from 'jspdf-autotable';

const CARBON = [23, 26, 29];
const GRAY = [102, 112, 122];
const LINE = [221, 225, 229];
const MARGIN = 20;
const TOP = 28;
const BOTTOM = 30;

/** Layout only: no scenario values or financial arithmetic belong here. */
export function createBoardPackLayout(doc) {
  const width = doc.internal.pageSize.getWidth() - MARGIN * 2;
  // jsPDF's built-in font metrics can slightly underestimate accented glyphs.
  const textWidth = width - 2;
  const bottom = doc.internal.pageSize.getHeight() - BOTTOM;
  let y = TOP;
  const newPage = () => { doc.addPage(); y = TOP; };
  const ensure = (height) => { if (y + height > bottom) newPage(); };

  const text = (value, { size = 9, color = CARBON, bold = false, gap = 4 } = {}) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(String(value), textWidth);
    const leading = size * 0.352778 * 1.3;
    // Keep short paragraphs together; longer ones flow without footer collisions.
    ensure(Math.min(lines.length, 3) * leading);
    lines.forEach((line, index) => {
      // Avoid leaving just the final line of a paragraph on the next page.
      ensure(index === lines.length - 2 ? leading * 2 : leading);
      doc.text(line, MARGIN, y);
      y += leading;
    });
    y += gap;
  };

  const heading = (label, followingHeight = 16) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(label.toUpperCase(), textWidth);
    ensure(lines.length * 4.2 + 6 + followingHeight);
    doc.setTextColor(...GRAY);
    lines.forEach(line => { doc.text(line, MARGIN, y); y += 4.2; });
    doc.setDrawColor(...LINE);
    doc.line(MARGIN, y - 2, MARGIN + width, y - 2);
    y += 5;
  };

  const table = (options) => {
    autoTable(doc, {
      ...options,
      startY: y,
      margin: { left: MARGIN, right: MARGIN, top: TOP, bottom: BOTTOM },
      rowPageBreak: 'avoid',
      showHead: 'everyPage',
      styles: { ...options.styles, overflow: 'linebreak' },
    });
    y = doc.lastAutoTable.finalY + 8;
  };

  const keepTogether = (blocks, extraHeight = 0) => {
    const height = blocks.reduce((sum, { value, size = 9, gap = 4, bold = false }) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      return sum + doc.splitTextToSize(String(value), textWidth).length * size * 0.352778 * 1.3 + gap;
    }, extraHeight);
    ensure(Math.min(height, bottom - TOP));
  };

  return { text, heading, table, newPage, keepTogether };
}

/** Decorate only after pagination, including pages created by autoTable. */
export function finishBoardPackPages(doc, t) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let page = 1; page <= total; page += 1) {
    doc.setPage(page);
    doc.setFillColor(...CARBON);
    doc.rect(0, 0, pageW, 2.5, 'F');
    doc.setFillColor(82, 127, 25);
    doc.rect(0, 0, 42, 2.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...GRAY);
    doc.text('ISMSCALCULATOR', MARGIN, 16);
    doc.setDrawColor(...LINE);
    doc.line(MARGIN, pageH - 21, pageW - MARGIN, pageH - 21);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY);
    const label = t('boardPack.page', { page, total });
    const labelWidth = doc.getTextWidth(label);
    const footer = doc.splitTextToSize(
      `${t('sharedScenarios.footerBrand')} — ${t('sharedScenarios.footerTagline')}`,
      pageW - MARGIN * 2 - labelWidth - 10,
    );
    doc.text(footer, MARGIN, pageH - 15);
    doc.text(label, pageW - MARGIN, pageH - 15, { align: 'right' });
  }
}